import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { initLenis, getLenis } from "./lib/scroll";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import AnalyticsPageView from "./components/AnalyticsPageView";
import HomePage from "./pages/HomePage";
import CompanyPage from "./pages/CompanyPage";
import PortfolioPage from "./pages/PortfolioPage";
import WarRoomPage from "./pages/WarRoomPage";
import GovResearchPage from "./pages/GovResearchPage";
import NewsPage from "./pages/NewsPage";
import NewsArticlePage from "./pages/NewsArticlePage";
import ContactPage from "./pages/ContactPage";
import ProductPage from "./pages/ProductPage";
import TurfTalkPage from "./pages/TurfTalkPage";
import BudPage from "./pages/BudPage";
import PricingPage from "./pages/PricingPage";
import ControlShiftLanding from "./pages/ControlShiftLanding";
import NotFoundPage from "./pages/NotFoundPage";
import { RetailPage, MediaPage } from "./pages/PortfolioLanePage";

// Legacy Squarespace routes → canonical destinations, preserving query strings.
// Client-side permanent-redirect semantics; server/edge 308s are handled at the
// hosting layer where supported (see public/_redirects).
const LegacyRedirect = ({ to }) => {
    const { search } = useLocation();
    return <Navigate to={to + search} replace />;
};

const ScrollManager = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        const lenis = getLenis();
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                if (lenis) lenis.scrollTo(el, { offset: -96 });
                else el.scrollIntoView({ behavior: "smooth" });
                return;
            }
        }
        if (lenis) lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
};

function App() {
    useEffect(() => {
        initLenis();
    }, []);
    return (
        <div className="App bg-[#0a0a0a] min-h-screen text-white">
            <div className="grain-overlay" aria-hidden="true" />
            <BrowserRouter>
                <ScrollManager />
                <AnalyticsPageView />
                {/* Control Shift ONLY: temporary branded front door when served on the
                    product subdomain. Scoped exception approved by Peter — at full app
                    launch the hostname is re-pointed to the Control Shift deployment.
                    No hostname routing exists for any other product. */}
                {window.location.hostname === "controlshift.titanhavengroup.com" ? (
                    <ControlShiftLanding />
                ) : (
                    <>
                        <Header />
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/company" element={<CompanyPage />} />
                            <Route path="/portfolio" element={<PortfolioPage />} />
                            <Route path="/portfolio/retail" element={<RetailPage />} />
                            <Route path="/portfolio/media" element={<MediaPage />} />
                            <Route path="/portfolio/turf-talk-2" element={<TurfTalkPage />} />
                            <Route path="/portfolio/bud" element={<BudPage />} />
                            <Route path="/portfolio/:slug" element={<ProductPage />} />
                            <Route path="/warroom" element={<WarRoomPage />} />
                            <Route path="/government-research" element={<GovResearchPage />} />
                            <Route path="/news" element={<NewsPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                            <Route path="/news/:slug" element={<NewsArticlePage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            {/* Unlinked, noindexed preview path for the Control Shift front door */}
                            <Route path="/controlshift" element={<ControlShiftLanding />} />
                            {/* Legacy Squarespace routes — permanent canonical destinations. */}
                            <Route path="/manager-control" element={<LegacyRedirect to="/portfolio/control-shift" />} />
                            <Route path="/thgecosystems" element={<LegacyRedirect to="/portfolio" />} />
                            <Route path="/press" element={<LegacyRedirect to="/news" />} />
                            <Route path="/ai-systems" element={<LegacyRedirect to="/portfolio" />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        <Footer />
                    </>
                )}
            </BrowserRouter>
        </div>
    );
}

export default App;
